import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCommerceContext, listAdminPurchases } from '../../services/purchaseProgramApi';

const PAGE_SIZE = 25;

export default function PurchaseHistoryTab({ programs }) {
  const [filters, setFilters] = useState({ programId: '', userId: '', dateFrom: '', dateTo: '', isAdminOnly: '' });
  const [purchases, setPurchases] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ciNames, setCiNames] = useState({});  // { uuid: display name }

  // Load CI site + customer names for badge resolution
  useEffect(() => {
    getCommerceContext().then(data => {
      const map = {};
      (data?.sites || []).forEach(s => { map[s.id] = s.name; });
      (data?.sites || []).forEach(site => {
        getCommerceContext(site.id).then(cd => {
          (cd?.customers || []).forEach(c => {
            map[c.id] = `${c.name}${c.erp_customer_id ? ` (${c.erp_customer_id})` : ''}`;
          });
          setCiNames(prev => ({ ...prev, ...map }));
        }).catch(() => {});
      });
      setCiNames(prev => ({ ...prev, ...map }));
    }).catch(() => {});
  }, []);

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const res = await listAdminPurchases({
        programId: filters.programId ? Number(filters.programId) : undefined,
        userId: filters.userId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        isAdminOnly: filters.isAdminOnly === 'admin' ? true : filters.isAdminOnly === 'self' ? false : undefined,
        limit: PAGE_SIZE,
        offset: off,
      });
      setPurchases(res?.items || []);
      setTotal(res?.total || 0);
      setOffset(off);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(0); }, []);

  const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (v) => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div>
      {/* Filters */}
      <div className="card mb-4" style={{ borderRadius: 10, border: '1px solid #e5e7eb' }}>
        <div className="card-body" style={{ padding: '14px 18px' }}>
          <div className="row g-2 align-items-end">
            <div className="col-sm-3">
              <label className="form-label fw-semibold" htmlFor="hist-prog" style={{ fontSize: 12 }}>Program</label>
              <select id="hist-prog" className="form-select form-select-sm" value={filters.programId} onChange={e => setFilters(f => ({ ...f, programId: e.target.value }))}>
                <option value="">All programs</option>
                {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="col-sm-2">
              <label className="form-label fw-semibold" htmlFor="hist-user" style={{ fontSize: 12 }}>User</label>
              <input id="hist-user" className="form-control form-control-sm" value={filters.userId} onChange={e => setFilters(f => ({ ...f, userId: e.target.value }))} placeholder="User ID" />
            </div>
            <div className="col-sm-2">
              <label className="form-label fw-semibold" htmlFor="hist-from" style={{ fontSize: 12 }}>From</label>
              <input id="hist-from" type="date" className="form-control form-control-sm" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
            </div>
            <div className="col-sm-2">
              <label className="form-label fw-semibold" htmlFor="hist-to" style={{ fontSize: 12 }}>To</label>
              <input id="hist-to" type="date" className="form-control form-control-sm" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
            </div>
            <div className="col-sm-2">
              <label className="form-label fw-semibold" htmlFor="hist-type" style={{ fontSize: 12 }}>Type</label>
              <select id="hist-type" className="form-select form-select-sm" value={filters.isAdminOnly} onChange={e => setFilters(f => ({ ...f, isAdminOnly: e.target.value }))}>
                <option value="">All</option>
                <option value="admin">Admin bulk</option>
                <option value="self">Self-service</option>
              </select>
            </div>
            <div className="col-sm-1">
              <button className="btn btn-primary btn-sm w-100" onClick={() => load(0)} style={{ background: '#1a56db', borderColor: '#1a56db' }}>
                {loading ? '…' : 'Go'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-hover mb-0" style={{ fontSize: 13, minWidth: 640 }}>
            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <tr>
                <th style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>Date</th>
                <th style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>User</th>
                <th style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>Customer / Site</th>
                <th style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>Program(s)</th>
                <th style={{ padding: '10px 14px', fontWeight: 600, color: '#374151', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>Type</th>
                <th style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>
                  <i className="bi bi-person-fill-gear me-1 text-warning" />Placed By
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" role="status" /></td></tr>
              )}
              {!loading && purchases.length === 0 && (
                <tr><td colSpan={8} className="text-center py-5" style={{ color: '#9ca3af' }}>No purchases found</td></tr>
              )}
              {!loading && purchases.map(p => (
                <tr key={p.id}>
                  <td style={{ padding: '10px 14px', color: '#374151', whiteSpace: 'nowrap' }}>{fmtDate(p.purchase_date)}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <code style={{ fontSize: 12, color: '#374151' }}>{p.user_id}</code>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12 }}>
                    {p.ship_to_id && (
                      <div style={{ marginBottom: 2 }}>
                        <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>
                          <i className="bi bi-globe me-1" />
                          {ciNames[p.ship_to_id] || p.ship_to_id}
                        </span>
                      </div>
                    )}
                    {p.customer_id && (
                      <span style={{ fontSize: 11, background: '#ede9fe', color: '#6d28d9', borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>
                        <i className="bi bi-building me-1" />
                        {ciNames[p.customer_id] || p.customer_id}
                      </span>
                    )}
                    {!p.customer_id && !p.ship_to_id && <span className="text-secondary">—</span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#6b7280', fontSize: 12 }}>
                    {Array.isArray(p.program_names) ? p.program_names.join(', ') : '—'}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#111827' }}>
                    {fmt$(p.total_amount)}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {p.proxy_user_id ? (
                      <span style={{ background: '#ede9fe', color: '#5b21b6', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>
                        <i className="bi bi-person-fill-gear me-1" />On Behalf
                      </span>
                    ) : p.is_admin_purchase ? (
                      <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>Admin</span>
                    ) : (
                      <span style={{ background: '#f0fdf4', color: '#057a55', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>Self</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12 }}>
                    {p.proxy_user_id ? (
                      <span title={`Proxy user ID: ${p.proxy_user_id}`}>
                        <i className="bi bi-person-fill-gear me-1 text-warning" />
                        {p.proxy_user_name || p.proxy_user_id}
                      </span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>{p.purchased_by || '—'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={offset === 0} onClick={() => load(Math.max(0, offset - PAGE_SIZE))}>← Prev</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={offset + PAGE_SIZE >= total} onClick={() => load(offset + PAGE_SIZE)}>
                Next<i className="bi bi-arrow-right ms-1" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
